<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import RichTextEditor from '@/shared/components/RichTextEditor.vue'
  import { Label } from '@/components/ui/label'
  import { type CustomerUser as User, AiSettingsService } from '@/types/generated'
  import { useUserStore } from '@/shared/stores/userStore'
  import { genericErrorToast, genericSuccessToast } from '@/lib/toasts'
  import { useTranslation } from '@/composables/useTranslation'
  import { debounce } from '@/lib/timing'
  import * as yup from 'yup'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'

  const { fetchUser } = useUserStore()
  const user = computed<User>(() => useUserStore().user)
  const { trackEvent } = useMetrics()

  // Translation setup
  const { t } = useTranslation({
    en: {
      title: 'Entity Extraction Instructions',
      description:
        'Configure instructions for the AI ​​to extract stakeholders from documents and emails. These instructions help the AI ​​identify relevant information such as names, addresses, dates, and other important details.',
      loading: 'Loading settings...',
      entityExtractionInstructions: 'Entity Extraction Instructions',
      entityExtractionInstructionsDescription:
        'These instructions help the AI ​​identify and extract stakeholders from incoming documents and emails. The AI ​​understands which information is important and what requires special attention.',
      entityExtractionInstructionsPlaceholder:
        'Describe how the AI ​​should identify and extract stakeholders from documents and emails...\nFor example: When an expert opinion in traffic law is uploaded, the client is always the named client from the opinion.',
      saveSettings: 'Save Settings',
      saving: 'Saving...',
      settingsSaved: 'Entity extraction settings successfully saved',
      loadingError: 'Error loading entity extraction settings',
      savingError: 'Error saving entity extraction settings',
      instructionsTooLong: 'Instructions may be a maximum of 10000 characters long'
    },
    de: {
      title: 'Anweisungen zur Beteiligtenextraktion',
      description:
        'Konfigurieren Sie Anweisungen für die KI zur Extraktion von Beteiligten aus Dokumenten und E-Mails. Diese Anweisungen helfen der KI, relevante Informationen wie Namen, Adressen, Daten und andere wichtige Details zu identifizieren.',
      loading: 'Lade Einstellungen...',
      entityExtractionInstructions: 'Anweisungen zur Beteiligtenextraktion',
      entityExtractionInstructionsDescription:
        'Diese Anweisungen helfen der KI bei der Erkennung und Extraktion von Beteiligten aus eingehenden Dokumenten und E-Mails. Die KI versteht dabei, welche Informationen wichtig sind und worauf besonders zu achten ist.',
      entityExtractionInstructionsPlaceholder:
        'Beschreiben Sie, wie die KI Beteiligte aus Dokumenten und E-Mails identifizieren und extrahieren soll...\nZum Beispiel: Wenn ein Sachverständigengutachten im Verkehrsrecht hochgeladen wird, ist der Mandant immer der genannte Auftraggeber aus dem Gutachten.',
      saveSettings: 'Einstellungen speichern',
      saving: 'Speichern...',
      settingsSaved: 'Einstellungen zur Beteiligtenextraktion erfolgreich gespeichert',
      loadingError: 'Fehler beim Laden der Einstellungen zur Beteiligtenextraktion',
      savingError: 'Fehler beim Speichern der Einstellungen zur Beteiligtenextraktion',
      instructionsTooLong: 'Anweisungen dürfen maximal 10000 Zeichen lang sein'
    }
  })

  // State
  const aiSettings = ref(null)
  const isLoading = ref(true)
  const isSaving = ref(false)
  const hasInitiallyLoaded = ref(false)

  // Form data
  const formData = ref({
    entity_extraction_instructions: ''
  })

  // Validation schema
  const validationSchema = yup.object({
    entity_extraction_instructions: yup.string().max(10000, t('instructionsTooLong'))
  })

  const isFormValid = computed(() => {
    try {
      validationSchema.validateSync(formData.value)
      return true
    } catch {
      return false
    }
  })

  onMounted(async () => {
    const fetching = [fetchAiSettings()]
    if (!user.value) {
      fetching.push(fetchUser())
    }
    await Promise.all(fetching)
  })

  const fetchAiSettings = async () => {
    try {
      isLoading.value = true
      const response = await AiSettingsService.aiSettingsList({ limit: 1 })
      if (response.results && response.results.length > 0) {
        aiSettings.value = response.results[0]
        formData.value = {
          entity_extraction_instructions: aiSettings.value.entity_extraction_instructions || ''
        }
      }
    } catch (error) {
      console.error('Error fetching AI settings', error)
      genericErrorToast(t('loadingError'))
    } finally {
      isLoading.value = false
      // Mark as initially loaded after a short delay to prevent initial save trigger
      setTimeout(() => {
        hasInitiallyLoaded.value = true
      }, 500)
    }
  }

  const saveSettings = async () => {
    if (!aiSettings.value || isSaving.value || !isFormValid.value || !hasInitiallyLoaded.value)
      return

    try {
      isSaving.value = true
      validationSchema.validateSync(formData.value)

      await AiSettingsService.aiSettingsPartialUpdate({
        uuid: aiSettings.value.uuid,
        requestBody: {
          entity_extraction_instructions: formData.value.entity_extraction_instructions
        }
      })

      // Track PostHog event when entity extraction instructions are set
      // Note: Event type will be available in MetricEventType after types are regenerated
      trackEvent('Entity Extraction Instructions Set' as MetricEventType, {
        instructions_length: formData.value.entity_extraction_instructions?.length || 0,
        has_instructions: !!formData.value.entity_extraction_instructions
      })

      genericSuccessToast(t('settingsSaved'))
    } catch (error) {
      console.error('Error saving entity extraction settings', error)
      genericErrorToast(t('savingError'))
    } finally {
      isSaving.value = false
    }
  }

  // Debounced auto-save function
  const debouncedSave = debounce(saveSettings, 1000)

  // Watch for changes in the form data to auto-save
  watch(
    () => formData.value.entity_extraction_instructions,
    () => {
      if (hasInitiallyLoaded.value) {
        debouncedSave()
      }
    }
  )
</script>

<template>
  <SettingsPage v-if="user">
    <template #actions></template>

    <div
      v-if="isLoading"
      class="flex items-center justify-center py-8"
    >
      <p class="text-gray-500">{{ t('loading') }}</p>
    </div>

    <div
      v-else
      class="space-y-6"
    >
      <!-- Header -->
      <div class="space-y-1">
        <h2 class="text-2xl font-semibold text-gray-900">{{ t('title') }}</h2>
        <p class="text-sm text-gray-600">{{ t('description') }}</p>
      </div>

      <!-- Entity Extraction Instructions -->
      <div class="space-y-4">
        <div class="space-y-2">
          <Label
            for="entity_extraction_instructions"
            class="p-0"
          >
            {{ t('entityExtractionInstructions') }}
          </Label>
          <p class="text-sm text-gray-600">
            {{ t('entityExtractionInstructionsDescription') }}
          </p>
          <div class="rich-text-editor-wrapper">
            <RichTextEditor
              v-model="formData.entity_extraction_instructions"
              :placeholder="t('entityExtractionInstructionsPlaceholder')"
              :disabled="isSaving"
              :enable-text-styling-buttons="false"
              :enable-code-view-button="false"
              :custom-toolbar-buttons="['bold', 'italic', 'underline']"
            />
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  .rich-text-editor-wrapper :deep(.fr-element) {
    min-height: 300px !important;
    height: 300px !important;
  }

  .rich-text-editor-wrapper :deep(.fr-placeholder) {
    white-space: pre-wrap !important;
    overflow: visible !important;
    text-overflow: clip !important;
    display: block !important;
  }

  .rich-text-editor-wrapper :deep(.fr-wrapper) {
    min-height: 300px !important;
  }
</style>
